// components/Pages.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

export default function Pages({ websiteId, section, initialSlug }) {
  const { pageSlug: routeSlug } = useParams();
  const pageSlug = (initialSlug ?? routeSlug ?? "").trim();

  const [page, setPage] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });

  useEffect(() => {
    if (!websiteId || !pageSlug) return;
    let cancelled = false;

    const normalize = (s = "") =>
      s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

    (async () => {
      setState({ loading: true, error: null });

      // Helper: resolve from a specific section id
      const tryResolve = async (contentId) => {
        try {
          const { item } = await api.resolveItem(contentId, pageSlug);
          return item || null;
        } catch {
          return null;
        }
      };

      try {
        // 1) Try the provided section first (fast path)
        if (section?._id) {
          const item = await tryResolve(section._id);
          if (item) {
            if (!cancelled) {
              setPage(item);
              setState({ loading: false, error: null });
            }
            return;
          }
        }

        // 2) Fetch all sections for this website and find page-like sections
        const secs = await api.sectionsByWebsite(websiteId);
        const candidates = [];
        const seen = new Set();

        // put the provided section (if any) first, then page-like ones
        if (section?._id && !seen.has(section._id)) {
          seen.add(section._id);
          candidates.push(section);
        }

        (secs || []).forEach((s) => {
          const slug = normalize(s.slug || "");
          const type = normalize(s.type || "");
          const looksPages =
            slug === "sidor" || type === "pages" || slug.includes("page");
          if (looksPages && !seen.has(s._id)) {
            seen.add(s._id);
            candidates.push(s);
          }
        });

        // Safety: if nothing matched, try all sections as a last resort
        if (candidates.length === 0) {
          (secs || []).forEach((s) => {
            if (!seen.has(s._id)) {
              seen.add(s._id);
              candidates.push(s);
            }
          });
        }

        // 3) Resolve sequentially (to avoid spamming 404s in dev)
        let found = null;
        for (const s of candidates) {
          // quick guard: if section exposes items list, we could pre-check, but
          // the resolver is already indexed — just try it.
          // If you want to pre-filter, you can list items and look for link === pageSlug.
          // const items = await api.itemsBySection(s._id)
          // if (!items?.some(it => normalize(it.link) === normalize(pageSlug))) continue;

          // try to resolve in this section
          found = await tryResolve(s._id);
          if (found) break;
        }

        if (found) {
          if (!cancelled) {
            setPage(found);
            setState({ loading: false, error: null });
          }
          return;
        }

        // 4) Fallback: one-shot resolver with the known slug "sidor" (common case)
        try {
          const { item } = await api.resolveBySlugs(websiteId, "sidor", pageSlug);
          if (item) {
            if (!cancelled) {
              setPage(item);
              setState({ loading: false, error: null });
            }
            return;
          }
        } catch {
          // ignore; we'll error out below
        }

        // Nothing found
        if (!cancelled) {
          setPage(null);
          setState({ loading: false, error: "Sidan hittades inte." });
        }
      } catch (e) {
        if (!cancelled) {
          setPage(null);
          setState({
            loading: false,
            error: e?.message || "Network error",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [websiteId, section?._id, section?.slug, pageSlug]);

  if (state.loading) return <div className="p-8">Laddar sida…</div>;
  if (state.error) return <div className="p-8 text-red-600">{state.error}</div>;
  if (!page) return <div className="p-8">Sidan hittades inte.</div>;

  return (
    <section className="p-8 min-h-[50vh] w-fit mx-auto lg:pb-16 lg:px-48 portrait:min-h-[57vh]">
      <h2 className="text-2xl font-bold mb-3">{page.name}</h2>
      <div dangerouslySetInnerHTML={{ __html: page.content || "" }} />
    </section>
  );
}
